import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Brain, Flame, Clock, TrendingUp, Hash, Swords, Gift, Users, Store } from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import PostCard from '../components/PostCard';
import Composer from '../components/Composer';
import { SkeletonList, Empty, HeroPortrait, Points, ShopCard, SectionHead } from '../components/ui';

const SORTS = [['hot', 'Hot', Flame], ['new', 'New', Clock], ['top', 'Top', TrendingUp]];

export default function Feed() {
  const { user, hero, city, guild } = useAuth();
  const [sort, setSort] = useState('hot');
  const [scope, setScope] = useState('all'); // all | city

  const { data: posts, isLoading } = useQuery({
    queryKey: ['feed', sort, scope, city?.handle],
    queryFn: () => api.get('/posts', {
      params: { sort, limit: 25, ...(scope === 'city' && city ? { group: city.handle } : {}) },
    }).then(r => r.data),
  });

  const { data: topShops } = useQuery({
    queryKey: ['topShops', city?.id],
    queryFn: () => api.get('/shops', { params: { limit: 4, sort: 'community', ...(city ? { city: city.handle } : {}) } }).then(r => r.data),
  });

  const { data: lb } = useQuery({
    queryKey: ['miniLb', city?.handle],
    queryFn: () => api.get('/leaderboard', { params: { scope: city ? `city:${city.handle}` : 'global', limit: 5 } }).then(r => r.data),
  });

  return (
    <div className="lg:grid lg:grid-cols-[1fr_300px] lg:gap-6">
      <div className="space-y-4">
        {/* HERO STRIP */}
        <div className="card p-3 flex items-center gap-3 bg-gradient-to-r from-brand-50 to-white">
          <Link to="/hero"><HeroPortrait hero={hero} size="sm" /></Link>
          <div className="flex-1 min-w-0">
            <p className="font-extrabold text-sm truncate">{hero?.name || user?.username}</p>
            <p className="text-[11px] text-ink-500 font-semibold">
              {city?.handle || 'No city'} {guild && `· !${guild.tag}`}
            </p>
            <div className="flex gap-1.5 mt-1.5">
              <Points value={user?.points_balance} size="sm" />
              <span className="chip-ink">⚔️ {hero?.elo || 1000}</span>
            </div>
          </div>
          <Link to="/arena" className="btn-primary btn-sm shrink-0"><Swords size={14} /> Fight</Link>
        </div>

        <Composer groupHandle={scope === 'city' ? city?.handle : undefined} />

        {/* FILTERS */}
        <div className="flex items-center gap-2 sticky top-14 z-20 py-2 -mx-3 px-3 bg-ink-50/90 backdrop-blur">
          <div className="flex bg-white rounded-xl p-1 border border-ink-100 shadow-soft">
            {SORTS.map(([k, l, Icon]) => (
              <button key={k} onClick={() => setSort(k)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition
                  ${sort === k ? 'bg-ink-900 text-white' : 'text-ink-500'}`}>
                <Icon size={12} /> {l}
              </button>))}
          </div>
          {city && (
            <button onClick={() => setScope(s => s === 'city' ? 'all' : 'city')}
              className={`px-3 py-2 rounded-xl text-xs font-bold border transition
                ${scope === 'city' ? 'bg-brand-500 text-white border-brand-500' : 'bg-white border-ink-200 text-ink-500'}`}>
              <Hash size={11} className="inline" /> {city.name}
            </button>
          )}
        </div>

        {isLoading ? <SkeletonList n={3} h="h-56" />
          : !posts?.length ? (
            <Empty icon={Hash} title="Nothing here yet"
              sub="Join your city group and be the first to post."
              action={<Link to="/groups" className="btn-primary btn-sm">Browse cities</Link>} />
          ) : posts.map(p => <PostCard key={p.id} post={p} />)}
      </div>

      {/* SIDEBAR */}
      <aside className="hidden lg:block space-y-4">
        <div className="sticky top-20 space-y-4">
          <div className="card p-4 bg-gradient-to-br from-grape-500 to-brand-600 text-white">
            <Gift size={20} className="mb-2" />
            <p className="font-display font-extrabold">Invite & earn</p>
            <p className="text-xs text-white/80 mt-1">Get 150 points per friend + 10% of everything they spend.</p>
            <Link to="/referrals" className="btn bg-white text-brand-700 btn-sm w-full mt-3">Get my link</Link>
          </div>
			
		  <Link to="/trivia" className="card p-4 bg-gradient-to-br from-indigo-500 to-grape-600 text-white block">
			  <Brain size={20} className="mb-2" />
			  <p className="font-display font-extrabold">City Trivia</p>
			  <p className="text-xs text-white/80 mt-1">Answer questions about your city and earn points for your hero.</p>
			  <span className="btn bg-white text-grape-700 btn-sm w-full mt-3">Play now</span>
		   </Link>

          {topShops?.length > 0 && (
            <div>
              <SectionHead title="Popular shops" right={<Link to="/shops" className="text-xs font-bold text-brand-600">All</Link>} />
              <div className="space-y-2">{topShops.map(s => <ShopCard key={s.id} shop={s} />)}</div>
            </div>
          )}

          {lb?.rows?.length > 0 && (
            <div>
              <SectionHead title={city ? `Top in ${city.name}` : 'Top heroes'}
                right={<Link to="/leaderboard" className="text-xs font-bold text-brand-600">All</Link>} />
              <div className="card p-2 space-y-1">
                {lb.rows.slice(0, 5).map((u, i) => (
                  <Link key={u.id} to={`/u/${u.username}`} className="flex items-center gap-2.5 p-2 rounded-xl hover:bg-ink-50">
                    <span className="w-5 text-center font-black text-xs text-ink-400">{i + 1}</span>
                    <img src={u.image_url || u.avatar_url || `https://api.dicebear.com/9.x/adventurer/svg?seed=${u.username}`}
                      className="w-8 h-8 rounded-lg object-cover" alt="" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate">{u.hero_name || u.username}</p>
                      <p className="text-[10px] text-ink-400">L{u.level} · {compact(u.score)} pts</p>
                    </div>
                  </Link>))}
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}